import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'Prof. Dr. Nilüfer Yıldırım';
const DEFAULT_DESC = 'Prof. Dr. Nilüfer Yıldırım Fonksiyonel Tıp Kliniği - Sağlığınıza bütüncül yaklaşım, kişiye özel çözümler. Fonksiyonel tıp, hormonal antiaging, koruyucu hekimlik ve stres yönetimi.';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_dr-nilufershop/artifacts/rd2kgze0_image.png';
const BASE_URL = window.location.origin;

function setMeta(property, content) {
  if (!content) return;
  const isOg = property.startsWith('og:') || property.startsWith('article:');
  let el = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(isOg ? 'property' : 'name', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(url) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

function setJsonLd(schemas) {
  document.querySelectorAll('script[data-seo-jsonld]').forEach(el => el.remove());
  const list = Array.isArray(schemas) ? schemas.filter(Boolean) : schemas ? [schemas] : [];
  list.forEach(schema => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', 'true');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  });
}

/**
 * SEO component — handles title, meta, OG/Twitter tags, canonical, and JSON-LD
 */
export function SEO({ title, description, image, type = 'website', schema, canonical, article }) {
  const { pathname } = useLocation();

  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Fonksiyonel Tıp`;
    const desc = description || DEFAULT_DESC;
    const ogImage = image || DEFAULT_IMAGE;
    const canonicalUrl = canonical || `${BASE_URL}${pathname}`;

    document.title = fullTitle;

    // Standard meta
    setMeta('description', desc);
    setMeta('author', SITE_NAME);
    setMeta('robots', pathname === '/admin' ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1');

    // Open Graph
    setMeta('og:title', fullTitle);
    setMeta('og:description', desc);
    setMeta('og:image', ogImage);
    setMeta('og:image:width', '1200');
    setMeta('og:image:height', '630');
    setMeta('og:url', canonicalUrl);
    setMeta('og:type', type);
    setMeta('og:site_name', SITE_NAME);
    setMeta('og:locale', 'tr_TR');

    // Article-specific OG tags
    if (type === 'article' && article) {
      setMeta('article:published_time', article.publishedTime);
      setMeta('article:author', SITE_NAME);
      setMeta('article:section', article.section || 'Sağlık');
    }

    // Twitter Card
    setMeta('twitter:card', ogImage ? 'summary_large_image' : 'summary');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', desc);
    setMeta('twitter:image', ogImage);

    // Geo tags (Ankara, Turkey)
    setMeta('geo.region', 'TR-06');
    setMeta('geo.placename', 'Ankara');

    setCanonical(canonicalUrl);
    setJsonLd(schema);

    return () => {
      document.querySelectorAll('script[data-seo-jsonld]').forEach(el => el.remove());
    };
  }, [title, description, image, type, schema, canonical, pathname, article]);

  return null;
}

/* ─── Schema.org Helpers ─── */

export function createMedicalBusinessSchema(settings) {
  if (!settings) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalBusiness',
    '@id': `${BASE_URL}/#organization`,
    name: settings.doctor?.name || SITE_NAME,
    description: DEFAULT_DESC,
    image: settings.doctor?.image || DEFAULT_IMAGE,
    url: BASE_URL,
    telephone: settings.contact?.phone,
    email: settings.contact?.email,
    priceRange: '$$',
    medicalSpecialty: 'Fonksiyonel Tıp',
    address: {
      '@type': 'PostalAddress',
      streetAddress: settings.contact?.address,
      addressLocality: 'Ankara',
      addressRegion: 'Ankara',
      addressCountry: 'TR'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '39.8821',
      longitude: '32.8127'
    },
    openingHoursSpecification: settings.contact?.hours ? {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00'
    } : undefined,
    sameAs: [
      settings.social?.youtube,
      settings.social?.instagram,
      settings.social?.facebook
    ].filter(Boolean)
  };
}

export function createPersonSchema(settings) {
  if (!settings?.doctor) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${BASE_URL}/#person`,
    name: settings.doctor.name,
    jobTitle: settings.doctor.title,
    description: settings.doctor.bio,
    image: settings.doctor.image,
    url: `${BASE_URL}/hakkimizda`,
    alumniOf: {
      '@type': 'EducationalOrganization',
      name: 'Tıp Fakültesi'
    },
    knowsAbout: ['Fonksiyonel Tıp', 'Hormonal Antiaging', 'Koruyucu Hekimlik', 'Stres Yönetimi'],
    worksFor: {
      '@type': 'MedicalBusiness',
      name: `${settings.doctor.name} Fonksiyonel Tıp Kliniği`
    }
  };
}

export function createFAQSchema(faqItems) {
  if (!faqItems?.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.q || item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a || item.answer
      }
    }))
  };
}

export function createBlogPostSchema(post) {
  if (!post) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${BASE_URL}/icerikler/${post.id}`,
    headline: post.title,
    description: post.summary,
    image: post.image,
    datePublished: post.date,
    dateModified: post.updated_at || post.date,
    inLanguage: 'tr',
    author: {
      '@type': 'Person',
      '@id': `${BASE_URL}/#person`,
      name: SITE_NAME
    },
    publisher: {
      '@type': 'Organization',
      '@id': `${BASE_URL}/#organization`,
      name: `${SITE_NAME} Fonksiyonel Tıp Kliniği`,
      logo: {
        '@type': 'ImageObject',
        url: DEFAULT_IMAGE
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${BASE_URL}/icerikler/${post.id}`
    }
  };
}

export function createBreadcrumbSchema(items) {
  if (!items?.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url ? `${BASE_URL}${item.url}` : undefined
    }))
  };
}

export function createServiceSchema(service) {
  if (!service) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: service.title,
    description: service.short || service.overview,
    url: `${BASE_URL}/hizmetler/${service.id}`,
    bodyLocation: 'Genel',
    procedureType: 'https://schema.org/NoninvasiveProcedure',
    howPerformed: service.overview,
    provider: {
      '@type': 'MedicalBusiness',
      '@id': `${BASE_URL}/#organization`,
      name: SITE_NAME
    }
  };
}

export function createVideoSchema(video) {
  if (!video) return null;
  const videoId = extractYouTubeId(video.youtube_url || video.url);
  if (!videoId) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.title,
    description: video.title,
    thumbnailUrl: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    uploadDate: video.date || new Date().toISOString().split('T')[0],
    contentUrl: video.youtube_url || video.url,
    embedUrl: `https://www.youtube.com/embed/${videoId}`,
    publisher: {
      '@type': 'Organization',
      '@id': `${BASE_URL}/#organization`,
      name: SITE_NAME
    }
  };
}

export function createVideoListSchema(videos) {
  if (!videos?.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: videos.slice(0, 10).map((v, i) => {
      const videoId = extractYouTubeId(v.youtube_url || v.url);
      return {
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'VideoObject',
          name: v.title,
          thumbnailUrl: videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : undefined,
          contentUrl: v.youtube_url || v.url,
          embedUrl: videoId ? `https://www.youtube.com/embed/${videoId}` : undefined
        }
      };
    })
  };
}

export function createWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${BASE_URL}/#website`,
    url: BASE_URL,
    name: SITE_NAME,
    description: DEFAULT_DESC,
    inLanguage: 'tr',
    publisher: {
      '@type': 'Organization',
      '@id': `${BASE_URL}/#organization`
    }
  };
}

function extractYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}
